import { API } from "@/config/apiBase";
import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Lock, Eye, EyeOff, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenStatus, setTokenStatus] = useState(null); // "valid", "invalid", "expired", "used"
  const [isSuccess, setIsSuccess] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenStatus("invalid");
        setIsVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/password-reset/verify?token=${token}`);
        if (response.data.valid) {
          setTokenStatus("valid");
        } else {
          setTokenStatus(response.data.reason || "invalid");
        }
      } catch (error) {
        setTokenStatus("invalid");
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API}/auth/password-reset/confirm`, {
        token,
        new_password: password
      });
      setIsSuccess(true);
      toast.success("Password updated successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to reset password. Please try again.";
      toast.error(message);
      
      // If token is invalid/expired/used, update status
      if (error.response?.status === 400) {
        setTokenStatus("invalid");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state while verifying token
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-4 border-[#E07A5F] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-[#6D6F7C]">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-8">
        <motion.div 
          className="w-full max-w-md text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#F4F1EA]">
            <div className="w-16 h-16 bg-[#81B29A]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#81B29A]" />
            </div>
            
            <h1 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              Password Updated!
            </h1>
            
            <p className="text-[#6D6F7C] mb-6">
              Your password has been successfully reset. You can now sign in with your new password.
            </p>
            
            <Button
              onClick={() => navigate("/login")}
              className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full h-12"
              data-testid="go-to-login-button"
            >
              Sign In
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Invalid/Expired/Used token state
  if (tokenStatus !== "valid") {
    const errorMessages = {
      invalid: "This reset link is invalid.",
      expired: "This reset link has expired.",
      used: "This reset link has already been used."
    };
    
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-8">
        <motion.div 
          className="w-full max-w-md text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#F4F1EA]">
            <div className="w-16 h-16 bg-[#E07A5F]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-[#E07A5F]" />
            </div>
            
            <h1 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              Link Not Valid
            </h1>
            
            <p className="text-[#6D6F7C] mb-6">
              {errorMessages[tokenStatus] || errorMessages.invalid} Please request a new password reset link.
            </p>
            
            <div className="space-y-3">
              <Link to="/forgot-password" className="block">
                <Button
                  className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full h-12"
                  data-testid="request-new-link-button"
                >
                  Request New Link
                </Button>
              </Link>
              
              <Link to="/login" className="block">
                <Button
                  variant="outline"
                  className="w-full border-2 border-[#F4F1EA] hover:bg-[#F4F1EA] rounded-full h-12 text-[#3D405B]"
                >
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Valid token - show password reset form
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-8">
      <motion.div 
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link to="/login" className="inline-flex items-center text-[#6D6F7C] hover:text-[#3D405B] mb-8 transition-colors" data-testid="back-link">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to login
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-[#E07A5F] rounded-full flex items-center justify-center">
            <Phone className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
            Set New Password
          </h1>
        </div>
        
        <p className="text-[#6D6F7C] mb-8">
          Enter your new password below. Make sure it's at least 8 characters.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="password" className="text-[#3D405B]">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-12 pr-12 bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl h-14 text-[#3D405B]"
                placeholder="••••••••"
                required
                minLength={8}
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#3D405B] transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-[#3D405B]">Confirm New Password</Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-12 pr-12 bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl h-14 text-[#3D405B]"
                placeholder="••••••••"
                required
                minLength={8}
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#3D405B] transition-colors"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {password && confirmPassword && password !== confirmPassword && (
              <p className="text-sm text-[#E07A5F]">Passwords don't match</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isLoading || password !== confirmPassword || password.length < 8}
            className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full h-14 text-lg font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
            data-testid="submit-button"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                Updating...
              </span>
            ) : (
              "Update Password"
            )}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
